import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { SystemUserService } from '../../Modules/Users/Services/system-user.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { ErrorHandlerService } from '../../Modules/Shared/Services/error-handler.service';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { OperatorFacade } from '../operator.facade';
import { SystemRole } from '../../Enums/system-role.enum';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { SystemUser } from '../../Models/backoffice/system-user.model';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import { SystemUserListResponse } from '../../Interfaces/api/backoffice/user.interface';

@Injectable()
export class TeamFacade {
  private readonly _systemUserService = inject(SystemUserService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _errorHandler = inject(ErrorHandlerService);
  private readonly _msgService = inject(MsgService);
  private readonly _operatorFacade = inject(OperatorFacade);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _isMutating = signal<boolean>(false);

  private readonly _operators = httpResource<LaravelDefaultResponse<SystemUserListResponse>>(() =>
    this._systemUserService.listRequest(),
  );

  readonly isMutating = computed<boolean>(() => this._isMutating());

  readonly operators = computed<SystemUser[]>(() =>
    this._systemUserService.toList(this._resourceState.valueOf(this._operators)),
  );

  readonly total = computed<number>(() => this._systemUserService.toTotal(this._resourceState.valueOf(this._operators)));

  readonly state = computed<MetricState>(() =>
    this._resourceState.metricState(this._operators.status()),
  );

  readonly isEmpty = computed<boolean>(
    () => this.state() === 'ready' && this.operators().length === 0,
  );

  readonly canAssignRoles = computed<boolean>(() =>
    this._operatorFacade.atLeast(SystemRole.BACKOFFICE_ADMIN),
  );

  readonly currentOperatorId = computed<string>(() => this._operatorFacade.operator()?.id ?? '');

  readonly adminCount = computed<number>(
    () =>
      this.operators().filter((operator) => operator.systemRole === SystemRole.BACKOFFICE_ADMIN)
        .length,
  );

  reload(): void {
    this._operators.reload();
  }

  updateSystemRole(userId: string, systemRole: SystemRole | null, force: boolean): void {
    this._isMutating.set(true);

    this._systemUserService
      .updateSystemRole(userId, systemRole, force)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterRoleChange(),
        error: (error) => this._failMutation(error),
      });
  }

  private _afterRoleChange(): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar('backoffice.users.team.role_changed', { type: 'success' });
    this.reload();
  }

  private _failMutation(error: unknown): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar(this._errorHandler.messageFor(error), { type: 'error' });
  }
}
